/**
 * WHETHER A CLUB PUBLISHES ITS FULL CALENDAR — one rule, read by every door to it.
 *
 * A club may decide it does not want its whole week visible to strangers (owner
 * decision 2026-08-16). Three surfaces offer that door — the club page's sidebar, its
 * Tournaments / Ladders empty states, and the "Open schedule" CTA on every club card in
 * the directory and on the home page — and the page itself is a fourth. They all ask
 * here, so the answer cannot drift between them.
 *
 * Default is VISIBLE: the field is absent on every club that existed before the
 * setting, and `publicScheduleHidden !== true` keeps all of them exactly as they are.
 * Hiding is the opt-out.
 *
 * This is a PUBLISHING choice, not a secret. The day-grid endpoint behind the page is
 * the same auth-optional read the club's own availability strip is built on, and that
 * strip stays open — it is how players book. Hiding removes the club's whole-calendar
 * page from the site; it does not classify the data.
 */
export type PublicScheduleVisibility = { publicScheduleHidden?: boolean } | null | undefined

export function isPublicScheduleOpen(club: PublicScheduleVisibility): boolean {
  return club?.publicScheduleHidden !== true
}

/**
 * What the club page last learned, kept per org for the session so the organization
 * page's own gate can answer without a second round trip when the visitor arrived
 * from the club page (the overwhelmingly common path). Written by the public-club
 * reads in ./api.ts; `undefined` simply means "not asked yet".
 */
const known = new Map<string, { hidden: boolean; name: string }>()

export function rememberPublicScheduleVisibility(
  club: { orgId?: string; name?: string } & PublicScheduleVisibility,
): void {
  const orgId = String(club?.orgId || '').trim()
  if (!orgId) return
  known.set(orgId, { hidden: !isPublicScheduleOpen(club), name: String(club?.name || '').trim() })
}

/** True = hidden, false = published, undefined = nobody has asked yet. */
export function readKnownPublicScheduleHidden(orgId: string): boolean | undefined {
  return known.get(String(orgId || '').trim())?.hidden
}

/** The club's public name from the same read; '' = row had none, undefined = not asked yet. */
export function readKnownPublicClubName(orgId: string): string | undefined {
  return known.get(String(orgId || '').trim())?.name
}
